import { useState } from "react";
import type { ReactNode } from "react";

import { useLocalization } from "@fluent/react";
import {
  Chip,
  LoadingSkeleton,
  Pagination,
  Popover,
  Table,
  useBreakpoints,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";

import styles from "./TransactionTable.module.scss";

type ColumnBreakpoint = "*" | "m" | "l" | "none";
type Viewport = "s" | "m" | "l";

export interface TransactionTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  minBreakpoint?: ColumnBreakpoint;
  width?: Record<Viewport, string>;
  textAlign?: "start" | "end";
}

export interface TransactionTableProps<T> {
  columns: TransactionTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  renderMobileRow: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  isLoading: boolean;
  emptyState: ReactNode;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  headerFill?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
}

const gridTemplateFor = <T,>(
  columns: TransactionTableColumn<T>[],
  viewport: Viewport
) => columns.map((column) => column.width?.[viewport] ?? "1fr");

const TransactionTable = <T,>({
  columns,
  rows,
  getRowId,
  renderMobileRow,
  onRowClick,
  isLoading,
  emptyState,
  page,
  totalPages,
  onPageChange,
  headerFill = true,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}: TransactionTableProps<T>) => {
  const { l10n } = useLocalization();
  const { m: isTableViewport } = useBreakpoints();
  const [isPageSizeOpen, setIsPageSizeOpen] = useState(false);

  const showPageSizeSelect =
    pageSize !== undefined &&
    onPageSizeChange !== undefined &&
    (pageSizeOptions?.length ?? 0) > 1;

  if (isLoading) {
    return (
      <LoadingSkeleton
        isLoading
        content="paragraph"
        lines={5}
        showTitle
        testId="transaction-table-loading"
      />
    );
  }

  if (rows.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <div
      className={cc(styles.tableContainer, {
        [styles.plainHeader]: !headerFill,
      })}
    >
      {isTableViewport ? (
        <Table
          colVisibility={columns.map((column) => column.minBreakpoint ?? "*")}
          colLayout={{
            s: gridTemplateFor(columns, "s"),
            m: gridTemplateFor(columns, "m"),
            l: gridTemplateFor(columns, "l"),
          }}
        >
          <Table.Header>
            <Table.Row>
              {columns.map((column) => (
                <Table.HeaderCell key={column.id} textAlign={column.textAlign}>
                  {column.header}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Table.Row
                key={getRowId(row)}
                onRowClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((column) => (
                  <Table.Cell key={column.id} textAlign={column.textAlign}>
                    {column.cell(row)}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      ) : (
        <ul
          className={styles.mobileList}
          data-testid="transaction-table-mobile-list"
        >
          {rows.map((row) => (
            <li key={getRowId(row)} className="border--bottom">
              {onRowClick ? (
                <button
                  type="button"
                  className={`button--reset ${styles.mobileRowButton}`}
                  onClick={() => onRowClick(row)}
                >
                  {renderMobileRow(row)}
                </button>
              ) : (
                renderMobileRow(row)
              )}
            </li>
          ))}
        </ul>
      )}
      {(totalPages > 1 || showPageSizeSelect) && (
        <div className={styles.paginationContainer}>
          {showPageSizeSelect && <div className={styles.pageSizeSpacer} />}
          {totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              selectedPage={page}
              onPageChange={onPageChange}
              testId="transaction-table-pagination"
            />
          )}
          {showPageSizeSelect && (
            <div
              className={styles.pageSizeSelect}
              data-testid="transaction-table-page-size"
            >
              <Popover
                isOpen={isPageSizeOpen}
                onUserEnable={() => setIsPageSizeOpen(true)}
                onUserDismiss={() => setIsPageSizeOpen(false)}
                closeOnContentClick
                testId="transaction-table-page-size-popover"
                renderTrigger={() => (
                  <Chip
                    kind="secondary"
                    label={l10n.getString("live-ledger-page-size-option", {
                      count: pageSize,
                    })}
                    endIcon={isPageSizeOpen ? "chevron-up" : "chevron-down"}
                    onClick={() => {}}
                  />
                )}
                content={
                  <div className={styles.pageSizeOptions} role="menu">
                    {(pageSizeOptions ?? []).map((option) => (
                      <button
                        type="button"
                        key={option}
                        className={styles.pageSizeOption}
                        data-testid={`transaction-table-page-size-option-${option}`}
                        role="menuitemradio"
                        aria-checked={pageSize === option}
                        onClick={() => onPageSizeChange(Number(option))}
                      >
                        {l10n.getString("live-ledger-page-size-option", {
                          count: option,
                        })}
                      </button>
                    ))}
                  </div>
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
